/**
 * Permanent Location Rendering System
 * 
 * Handles rendering of permanent locations (trading posts, harbor offices, shipyards, taverns).
 * Separated from IslandRenderer for clearer separation of concerns.
 */

import Phaser from 'phaser';
import { TILE_SIZE, CHUNK_SIZE, IslandData } from '../types';
import { drawSafeZone } from '../utils/RadiusRenderer';

// Trading post colors
const POST_COLOR = 0x87ceeb;
const POST_BORDER = 0x5dade2;

// Shipyard colors
const SHIPYARD_COLOR = 0xffa500;  // Orange
const SHIPYARD_BORDER = 0xff8c00;

// Tavern colors
const TAVERN_COLOR = 0x8b5a2b;
const TAVERN_BORDER = 0x6f4518;

export interface TradingPostInfo {
  x: number;
  y: number;
  name: string;
}

export interface ShipyardInfo {
  x: number;
  y: number;
  name: string;
}

export interface PermanentLocationRendererCallbacks {
  onTradingPostClick: (island: IslandData, worldX: number, worldY: number) => void;
  onShipyardClick?: (island: IslandData, worldX: number, worldY: number) => void;
  onHarborOfficeClick?: (island: IslandData, worldX: number, worldY: number) => void;
  onTavernClick?: (island: IslandData, worldX: number, worldY: number) => void;
  onIslandRightClick?: (island: IslandData, screenX: number, screenY: number) => void;
  getLocalPlayerId?: () => string;
}

interface PermanentLocationEntry {
  graphics: Phaser.GameObjects.Graphics;
  x: number;
  y: number;
  islandData: IslandData;
  hitArea?: Phaser.GameObjects.Rectangle;
  label?: Phaser.GameObjects.Text;
  typeLabel?: Phaser.GameObjects.Text;
  safeLabel?: Phaser.GameObjects.Text;
  safeZoneGraphics?: Phaser.GameObjects.Graphics;
}

export class PermanentLocationRenderer {
  private scene: Phaser.Scene;
  private locations: Map<string, PermanentLocationEntry> = new Map();
  private tradingPosts: Map<string, TradingPostInfo> = new Map();
  private callbacks: PermanentLocationRendererCallbacks;
  private loadedChunks: Set<string> = new Set();
  private isInRiftInstance: boolean = false;

  constructor(scene: Phaser.Scene, callbacks: PermanentLocationRendererCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  /**
   * Set whether we're in a rift instance
   * Permanent locations only exist in the core world
   */
  setIsInRiftInstance(isInRift: boolean): void {
    this.isInRiftInstance = isInRift;
    if (isInRift) {
      // Clear all permanent locations when entering a rift
      this.locations.forEach((entry) => {
        entry.graphics.destroy();
        entry.hitArea?.destroy();
        entry.label?.destroy();
        entry.typeLabel?.destroy();
      });
      this.locations.clear();
      this.tradingPosts.clear();
    }
  }

  getLocations(): Map<string, PermanentLocationEntry> {
    return this.locations;
  }

  getTradingPosts(): Map<string, TradingPostInfo> {
    return this.tradingPosts;
  }

  /**
   * Render a permanent location (trading post, harbor office, shipyard, or tavern)
   * NOTE: Permanent locations only exist in the CORE WORLD, not in rift instances
   */
  renderLocation(island: IslandData): void {
    // Don't render permanent locations in rift instances
    if (this.isInRiftInstance) {
      return;
    }
    
    if (this.locations.has(island.id)) return;
    
    // Check if this is a trading post by ID or name patterns
    const isTradingPost = this.isTradingPostIsland(island);
    if (isTradingPost) {
      this.createTradingPostSprite(island);
      return;
    }

    // Check if this is a harbor office
    const isHarborOffice = this.isHarborOfficeIsland(island);
    if (isHarborOffice) {
      this.createHarborOfficeSprite(island);
      return;
    }

    // Check if this is a tavern
    const isTavern = this.isTavernIsland(island);
    if (isTavern) {
      this.createTavernSprite(island);
      return;
    }
    
    // Check if this is a shipyard
    const isShipyard = this.isShipyardIsland(island);
    if (isShipyard) {
      this.createShipyardSprite(island);
      return;
    }
  }

  /**
   * Remove a permanent location
   */
  removeLocation(islandId: string): void {
    const entry = this.locations.get(islandId);
    if (entry) {
      entry.graphics.destroy();
      entry.hitArea?.destroy();
      entry.label?.destroy();
      entry.typeLabel?.destroy();
      this.locations.delete(islandId);
      this.tradingPosts.delete(islandId);
    }
  }

  /**
   * Unload permanent locations from a chunk
   */
  unloadChunk(chunkId: string): void {
    const [cx, cy] = chunkId.split('_').map(Number);
    const chunkMinX = cx * CHUNK_SIZE;
    const chunkMaxX = (cx + 1) * CHUNK_SIZE;
    const chunkMinY = cy * CHUNK_SIZE;
    const chunkMaxY = (cy + 1) * CHUNK_SIZE;
    
    const locationsToRemove: string[] = [];
    this.locations.forEach((entry, locationId) => {
      // Use stored position to check if location is in chunk
      if (entry.x >= chunkMinX && entry.x < chunkMaxX && entry.y >= chunkMinY && entry.y < chunkMaxY) {
        locationsToRemove.push(locationId);
      }
    });
    
    for (const locationId of locationsToRemove) {
      this.removeLocation(locationId);
    }
    
    this.loadedChunks.delete(chunkId);
    console.log(`🗑️ Unloaded chunk ${chunkId} from PermanentLocationRenderer: removed ${locationsToRemove.length} locations`);
  }

  /**
   * Track that a chunk has been loaded
   */
  markChunkLoaded(chunkId: string): void {
    this.loadedChunks.add(chunkId);
  }

  /**
   * Check if a chunk is loaded
   */
  isChunkLoaded(chunkId: string): boolean {
    return this.loadedChunks.has(chunkId);
  }

  /**
   * Clear all permanent locations
   */
  clear(): void {
    this.locations.forEach(entry => {
      entry.graphics.destroy();
      entry.hitArea?.destroy();
      entry.label?.destroy();
      entry.typeLabel?.destroy();
    });
    this.locations.clear();
    this.tradingPosts.clear();
  }

  /**
   * Get location data by ID
   */
  getLocationData(locationId: string): IslandData | null {
    const entry = this.locations.get(locationId);
    return entry?.islandData || null;
  }

  /**
   * Check if an island is a permanent location
   */
  isPermanentLocation(island: IslandData): boolean {
    return this.isTradingPostIsland(island) ||
           this.isHarborOfficeIsland(island) ||
           this.isTavernIsland(island) ||
           this.isShipyardIsland(island);
  }

  /**
   * Check if an island is a trading post
   * Trading posts have specific IDs or name patterns
   */
  private isTradingPostIsland(island: IslandData): boolean {
    // Check by ID pattern (permanent_trading_post_X)
    if (island.id.includes('trading_post')) {
      return true;
    }
    
    const name = island.permanentName?.toLowerCase() || '';
    
    // Check for explicit "trading post" in name
    if (name.includes('trading post')) {
      return true;
    }
    
    // Check for known trading post names from seedPermanentLocations
    const tradingPostNames = [
      'coral exchange',
      'mariner\'s market',
      'great current bazaar',
      'charted haven',
      'frontier outpost',
    ];
    
    return tradingPostNames.some(tpName => name.includes(tpName));
  }

  /**
   * Check if an island is a shipyard
   */
  private isShipyardIsland(island: IslandData): boolean {
    // Check by ID pattern (permanent_shipyard_X)
    if (island.id.includes('shipyard')) {
      return true;
    }
    
    const name = island.permanentName?.toLowerCase() || '';
    
    // Check for explicit "shipyard" in name
    if (name.includes('shipyard')) {
      return true;
    }
    
    // Check for known shipyard names from seedPermanentLocations
    const shipyardNames = [
      'eastern reach drydock',
      'western reach shipworks',
      'grand latitude shipyard',
      'arcadia forge',
    ];
    
    return shipyardNames.some(syName => name.includes(syName));
  }

  private isHarborOfficeIsland(island: IslandData): boolean {
    if (island.id.includes('harbor_office')) {
      return true;
    }
    const name = island.permanentName?.toLowerCase() || '';
    if (name.includes('harbor office') || name.includes('harbor master') || name.includes('port authority') || name.includes('fleet command')) {
      return true;
    }
    return false;
  }

  private isTavernIsland(island: IslandData): boolean {
    if (island.id.includes('tavern')) {
      return true;
    }
    const name = island.permanentName?.toLowerCase() || '';
    if (name.includes('tavern')) return true;
    return false;
  }

  private createTradingPostSprite(island: IslandData): void {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(3);
    
    const centerTileX = Math.floor(island.x / TILE_SIZE);
    const centerTileY = Math.floor(island.y / TILE_SIZE);
    
    const plusTiles = [
      { dx: 0, dy: -1 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 0 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
    ];
    
    const safeRadiusTiles = 8; // Safe zone radius in tiles (reduced from 10)
    
    const drawTiles = (fillColor: number, borderColor: number, borderWidth: number): void => {
      plusTiles.forEach(tile => {
        const worldX = (centerTileX + tile.dx) * TILE_SIZE;
        const worldY = (centerTileY + tile.dy) * TILE_SIZE;
        graphics.fillStyle(fillColor, 1);
        graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
        graphics.lineStyle(borderWidth, borderColor, 1);
        graphics.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
      });
    };
    
    // Draw grid-based safe zone (faded squares)
    const isInsideRift = this.scene.registry.get('riftId') && this.scene.registry.get('riftId') !== 'core';
    
    let safeZoneGraphics: Phaser.GameObjects.Graphics | undefined;
    if (!isInsideRift) {
      safeZoneGraphics = this.scene.add.graphics();
      safeZoneGraphics.setDepth(2);
      drawSafeZone(safeZoneGraphics, island.x, island.y, safeRadiusTiles);
    }
    
    drawTiles(POST_COLOR, POST_BORDER, 1);
    
    const postCenterX = centerTileX * TILE_SIZE + TILE_SIZE / 2;
    const postCenterY = centerTileY * TILE_SIZE + TILE_SIZE / 2;
    
    // Add decorative elements for Trading Post
    // Coin stacks on left and right tiles
    const leftTileX = (centerTileX - 1) * TILE_SIZE;
    const rightTileX = (centerTileX + 1) * TILE_SIZE;
    const centerTileWorldY = centerTileY * TILE_SIZE;
    
    // Left coin stack (3 coins)
    graphics.fillStyle(0xffd700, 1); // Gold
    graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 4, 6, 3);
    graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 7, 6, 3);
    graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 10, 6, 3);
    graphics.lineStyle(1, 0xb8860b, 1);
    graphics.strokeEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 4, 6, 3);
    graphics.strokeEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 7, 6, 3);
    graphics.strokeEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 10, 6, 3);
    
    // Right coin stack (3 coins)
    graphics.fillStyle(0xffd700, 1);
    graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 4, 6, 3);
    graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 7, 6, 3);
    graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 10, 6, 3);
    graphics.lineStyle(1, 0xb8860b, 1);
    graphics.strokeEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 4, 6, 3);
    graphics.strokeEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 7, 6, 3);
    graphics.strokeEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 10, 6, 3);
    
    // Merchant scale symbol in center tile
    const scaleY = postCenterY - 3;
    graphics.lineStyle(2, 0x4a90a4, 1);
    // Scale beam
    graphics.lineBetween(postCenterX - 8, scaleY, postCenterX + 8, scaleY);
    // Scale post
    graphics.lineBetween(postCenterX, scaleY, postCenterX, scaleY + 6);
    // Scale pans
    graphics.lineStyle(1, 0x4a90a4, 1);
    graphics.strokeRect(postCenterX - 10, scaleY, 4, 3);
    graphics.strokeRect(postCenterX + 6, scaleY, 4, 3);
    // Chains
    graphics.lineBetween(postCenterX - 8, scaleY, postCenterX - 8, scaleY + 3);
    graphics.lineBetween(postCenterX + 8, scaleY, postCenterX + 8, scaleY + 3);
    
    let safeLabel: Phaser.GameObjects.Text | undefined;
    if (!isInsideRift) {
      safeLabel = this.scene.add.text(postCenterX, postCenterY - TILE_SIZE * 2, 'SAFE ZONE', {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '8px',
        color: '#87ceeb',
        backgroundColor: '#000000aa',
        padding: { x: 2, y: 1 },
      });
      safeLabel.setOrigin(0.5);
      safeLabel.setDepth(4);
    }
    
    // Use the actual trading post name (e.g., "Charted Haven")
    const displayName = island.permanentName || 'Trading Post';
    const label = this.scene.add.text(postCenterX, postCenterY + TILE_SIZE * 1.5, displayName, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '9px',
      color: '#ffffff',
      backgroundColor: '#5dade2cc',
      padding: { x: 3, y: 1 },
    });
    label.setOrigin(0.5);
    label.setDepth(4);
    
    // Add secondary type label beneath the name
    const typeLabel = this.scene.add.text(postCenterX, postCenterY + TILE_SIZE * 2.5, 'Trading Post', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '7px',
      color: '#aaaaaa',
      backgroundColor: '#000000aa',
      padding: { x: 2, y: 1 },
    });
    typeLabel.setOrigin(0.5);
    typeLabel.setDepth(4);
    
    // Create interactive hit areas ONLY for the colored building tiles (not the safe zone)
    // Safe zone is visual only - clicks should work on water outside it
    const tileHitAreas: Phaser.GameObjects.Rectangle[] = [];
    plusTiles.forEach(tile => {
      const tileWorldX = (centerTileX + tile.dx) * TILE_SIZE;
      const tileWorldY = (centerTileY + tile.dy) * TILE_SIZE;
      const tileHitArea = this.scene.add.rectangle(tileWorldX + TILE_SIZE / 2, tileWorldY + TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 0x000000, 0);
      tileHitArea.setInteractive();
      tileHitArea.setDepth(6); // Same depth as resource nodes and fishing spots
      tileHitAreas.push(tileHitArea);
      
      // Consolidated click handler for both left and right clicks on individual tiles
      tileHitArea.on('pointerdown', (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        // Handle right-click FIRST before stopping propagation
        if (pointer.rightButtonDown()) {
          console.log(`🎯 Trading Post right-click detected!`);
          event.stopPropagation();
          if (pointer.event && typeof pointer.event.stopPropagation === 'function') {
            pointer.event.stopPropagation();
          }
          if (pointer.event && typeof pointer.event.preventDefault === 'function') {
            pointer.event.preventDefault();
          }
          
          if (this.callbacks.onIslandRightClick) {
            // Right-click handler for context menu
            const camera = this.scene.cameras.main;
            const screenX = pointer.worldX - camera.scrollX;
            const screenY = pointer.worldY - camera.scrollY;
            console.log(`📍 Calling onIslandRightClick with screenPos: (${screenX}, ${screenY})`);
            this.callbacks.onIslandRightClick(island, screenX, screenY);
          }
          return;
        }
        
        // For left-click, also stop propagation
        if (pointer.leftButtonDown()) {
          event.stopPropagation();
          if (pointer.event && typeof pointer.event.stopPropagation === 'function') {
            pointer.event.stopPropagation();
          }
          if (pointer.event && typeof pointer.event.preventDefault === 'function') {
            pointer.event.preventDefault();
          }
          
          console.log(`🏪 Trading Post tile left-clicked: ${island.permanentName}`);
          this.callbacks.onTradingPostClick(island, pointer.worldX, pointer.worldY);
        }
      });
      
      // Hover effects on building tiles
      tileHitArea.on('pointerover', () => {
        graphics.clear();
        drawSafeZone(graphics, island.x, island.y, safeRadiusTiles);
        drawTiles(0xb3e5fc, 0x81d4fa, 2);
        // Redraw decorations on hover
        // Coin stacks
        graphics.fillStyle(0xffd700, 1);
        const leftTileX = (centerTileX - 1) * TILE_SIZE;
        const rightTileX = (centerTileX + 1) * TILE_SIZE;
        const centerTileWorldY = centerTileY * TILE_SIZE;
        graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 4, 6, 3);
        graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 7, 6, 3);
        graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 10, 6, 3);
        graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 4, 6, 3);
        graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 7, 6, 3);
        graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 10, 6, 3);
        // Scale
        const scaleY = postCenterY - 3;
        graphics.lineStyle(2, 0x4a90a4, 1);
        graphics.lineBetween(postCenterX - 8, scaleY, postCenterX + 8, scaleY);
        graphics.lineBetween(postCenterX, scaleY, postCenterX, scaleY + 6);
        graphics.lineStyle(1, 0x4a90a4, 1);
        graphics.strokeRect(postCenterX - 10, scaleY, 4, 3);
        graphics.strokeRect(postCenterX + 6, scaleY, 4, 3);
        graphics.lineBetween(postCenterX - 8, scaleY, postCenterX - 8, scaleY + 3);
        graphics.lineBetween(postCenterX + 8, scaleY, postCenterX + 8, scaleY + 3);
      });
      
      tileHitArea.on('pointerout', () => {
        graphics.clear();
        drawSafeZone(graphics, island.x, island.y, safeRadiusTiles);
        drawTiles(POST_COLOR, POST_BORDER, 1);
        // Redraw decorations on normal state
        graphics.fillStyle(0xffd700, 1);
        const leftTileX = (centerTileX - 1) * TILE_SIZE;
        const rightTileX = (centerTileX + 1) * TILE_SIZE;
        const centerTileWorldY = centerTileY * TILE_SIZE;
        graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 4, 6, 3);
        graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 7, 6, 3);
        graphics.fillEllipse(leftTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 10, 6, 3);
        graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 4, 6, 3);
        graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 7, 6, 3);
        graphics.fillEllipse(rightTileX + TILE_SIZE / 2, centerTileWorldY + TILE_SIZE - 10, 6, 3);
        const scaleY = postCenterY - 3;
        graphics.lineStyle(2, 0x4a90a4, 1);
        graphics.lineBetween(postCenterX - 8, scaleY, postCenterX + 8, scaleY);
        graphics.lineBetween(postCenterX, scaleY, postCenterX, scaleY + 6);
        graphics.lineStyle(1, 0x4a90a4, 1);
        graphics.strokeRect(postCenterX - 10, scaleY, 4, 3);
        graphics.strokeRect(postCenterX + 6, scaleY, 4, 3);
        graphics.lineBetween(postCenterX - 8, scaleY, postCenterX - 8, scaleY + 3);
        graphics.lineBetween(postCenterX + 8, scaleY, postCenterX + 8, scaleY + 3);
      });
    });
    
    // Store first tile hit area for backward compatibility (used for cleanup)
    const hitArea = tileHitAreas[0];
    
    this.locations.set(island.id, { graphics, x: island.x, y: island.y, islandData: island, hitArea, label, typeLabel, safeLabel, safeZoneGraphics });
    this.tradingPosts.set(island.id, { x: island.x, y: island.y, name: 'Trading Post' });
  }

  private createHarborOfficeSprite(island: IslandData): void {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(3);

    const centerTileX = Math.floor(island.x / TILE_SIZE);
    const centerTileY = Math.floor(island.y / TILE_SIZE);

    const xTiles = [
      { dx: -1, dy: -1 },
      { dx: 0, dy: 0 },
      { dx: 1, dy: 1 },
      { dx: -1, dy: 1 },
      { dx: 1, dy: -1 },
    ];

    const safeRadiusTiles = 8;
    const markerColor = 0xfacc15;
    const markerBorder = 0xeab308;

    const drawTiles = (fillColor: number, borderColor: number, borderWidth: number): void => {
      xTiles.forEach(tile => {
        const worldX = (centerTileX + tile.dx) * TILE_SIZE;
        const worldY = (centerTileY + tile.dy) * TILE_SIZE;
        graphics.fillStyle(fillColor, 1);
        graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
        graphics.lineStyle(borderWidth, borderColor, 1);
        graphics.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
      });
    };

    const isInsideRift = this.scene.registry.get('riftId') && this.scene.registry.get('riftId') !== 'core';
    if (!isInsideRift) {
      drawSafeZone(graphics, island.x, island.y, safeRadiusTiles);
    }
    drawTiles(markerColor, markerBorder, 1);

    const centerX = centerTileX * TILE_SIZE + TILE_SIZE / 2;
    const centerY = centerTileY * TILE_SIZE + TILE_SIZE / 2;
    
    // Add decorative elements for Harbor Office
    // Compass rose in center
    const compassSize = 8;
    graphics.lineStyle(2, 0x92400e, 1);
    // North-South line
    graphics.lineBetween(centerX, centerY - compassSize, centerX, centerY + compassSize);
    // East-West line
    graphics.lineBetween(centerX - compassSize, centerY, centerX + compassSize, centerY);
    // Diagonal lines
    graphics.lineStyle(1, 0x92400e, 0.6);
    graphics.lineBetween(centerX - compassSize * 0.7, centerY - compassSize * 0.7, centerX + compassSize * 0.7, centerY + compassSize * 0.7);
    graphics.lineBetween(centerX - compassSize * 0.7, centerY + compassSize * 0.7, centerX + compassSize * 0.7, centerY - compassSize * 0.7);
    // North pointer (filled triangle)
    graphics.fillStyle(0xdc2626, 1);
    graphics.fillTriangle(
      centerX, centerY - compassSize - 2,
      centerX - 2, centerY - compassSize + 2,
      centerX + 2, centerY - compassSize + 2
    );
    
    // Small flags on the diagonal tiles
    const drawFlag = (x: number, y: number) => {
      graphics.lineStyle(1, 0x92400e, 1);
      // Pole
      graphics.lineBetween(x, y, x, y - 8);
      // Flag
      graphics.fillStyle(0xef4444, 1);
      graphics.fillTriangle(
        x, y - 8,
        x, y - 5,
        x + 5, y - 6.5
      );
      graphics.lineStyle(1, 0xb91c1c, 1);
      graphics.strokeTriangle(
        x, y - 8,
        x, y - 5,
        x + 5, y - 6.5
      );
    };
    
    // Flags on diagonal corners
    const tlX = (centerTileX - 1) * TILE_SIZE + TILE_SIZE - 3;
    const tlY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE - 3;
    const trX = (centerTileX + 1) * TILE_SIZE + 3;
    const trY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE - 3;
    drawFlag(tlX, tlY);
    drawFlag(trX, trY);

    let safeLabel: Phaser.GameObjects.Text | undefined;
    if (!isInsideRift) {
      safeLabel = this.scene.add.text(centerX, centerY - TILE_SIZE * 2, 'SAFE ZONE', {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '8px',
        color: '#facc15',
        backgroundColor: '#000000aa',
        padding: { x: 2, y: 1 },
      });
      safeLabel.setOrigin(0.5);
      safeLabel.setDepth(4);
    }

    const displayName = island.permanentName || 'Harbor Office';
    const label = this.scene.add.text(centerX, centerY + TILE_SIZE * 1.5, displayName, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '9px',
      color: '#ffffff',
      backgroundColor: '#a16207cc',
      padding: { x: 3, y: 1 },
    });
    label.setOrigin(0.5);
    label.setDepth(4);

    const typeLabel = this.scene.add.text(centerX, centerY + TILE_SIZE * 2.5, 'Harbor Office', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '7px',
      color: '#aaaaaa',
      backgroundColor: '#000000aa',
      padding: { x: 2, y: 1 },
    });
    typeLabel.setOrigin(0.5);
    typeLabel.setDepth(4);

    // Create interactive hit areas ONLY for the colored building tiles (not the safe zone)
    // Safe zone is visual only - clicks should work on water outside it
    const tileHitAreas: Phaser.GameObjects.Rectangle[] = [];
    xTiles.forEach(tile => {
      const tileWorldX = (centerTileX + tile.dx) * TILE_SIZE;
      const tileWorldY = (centerTileY + tile.dy) * TILE_SIZE;
      const tileHitArea = this.scene.add.rectangle(tileWorldX + TILE_SIZE / 2, tileWorldY + TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 0x000000, 0);
      tileHitArea.setInteractive();
      tileHitArea.setDepth(6); // Same depth as resource nodes and fishing spots
      tileHitAreas.push(tileHitArea);
      
      // Consolidated click handler for both left and right clicks on individual tiles
      tileHitArea.on('pointerdown', (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        // Stop propagation using both Phaser event and native DOM event
        event.stopPropagation();
        if (pointer.event && typeof pointer.event.stopPropagation === 'function') {
          pointer.event.stopPropagation();
        }
        if (pointer.event && typeof pointer.event.preventDefault === 'function') {
          pointer.event.preventDefault();
        }
        
        if (pointer.rightButtonDown() && this.callbacks.onIslandRightClick) {
          // Right-click handler for context menu
          const camera = this.scene.cameras.main;
          const screenX = pointer.worldX - camera.scrollX;
          const screenY = pointer.worldY - camera.scrollY;
          this.callbacks.onIslandRightClick(island, screenX, screenY);
          return;
        }
        
        if (pointer.leftButtonDown()) {
          console.log(`🏛️ Harbor Office tile clicked: ${island.permanentName}`);
          this.callbacks.onHarborOfficeClick?.(island, pointer.worldX, pointer.worldY);
        }
      });
      
      // Hover effects on building tiles
      tileHitArea.on('pointerover', () => {
        graphics.clear();
        drawSafeZone(graphics, island.x, island.y, safeRadiusTiles);
        drawTiles(0xfde047, 0xf59e0b, 2);
        // Redraw decorations
        const compassSize = 8;
        graphics.lineStyle(2, 0x92400e, 1);
        graphics.lineBetween(centerX, centerY - compassSize, centerX, centerY + compassSize);
        graphics.lineBetween(centerX - compassSize, centerY, centerX + compassSize, centerY);
        graphics.lineStyle(1, 0x92400e, 0.6);
        graphics.lineBetween(centerX - compassSize * 0.7, centerY - compassSize * 0.7, centerX + compassSize * 0.7, centerY + compassSize * 0.7);
        graphics.lineBetween(centerX - compassSize * 0.7, centerY + compassSize * 0.7, centerX + compassSize * 0.7, centerY - compassSize * 0.7);
        graphics.fillStyle(0xdc2626, 1);
        graphics.fillTriangle(centerX, centerY - compassSize - 2, centerX - 2, centerY - compassSize + 2, centerX + 2, centerY - compassSize + 2);
        const drawFlag = (x: number, y: number) => {
          graphics.lineStyle(1, 0x92400e, 1);
          graphics.lineBetween(x, y, x, y - 8);
          graphics.fillStyle(0xef4444, 1);
          graphics.fillTriangle(x, y - 8, x, y - 5, x + 5, y - 6.5);
        };
        const tlX = (centerTileX - 1) * TILE_SIZE + TILE_SIZE - 3;
        const tlY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE - 3;
        const trX = (centerTileX + 1) * TILE_SIZE + 3;
        const trY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE - 3;
        drawFlag(tlX, tlY);
        drawFlag(trX, trY);
      });
      
      tileHitArea.on('pointerout', () => {
        graphics.clear();
        drawSafeZone(graphics, island.x, island.y, safeRadiusTiles);
        drawTiles(0xfacc15, 0xeab308, 1);
        // Redraw decorations
        const compassSize = 8;
        graphics.lineStyle(2, 0x92400e, 1);
        graphics.lineBetween(centerX, centerY - compassSize, centerX, centerY + compassSize);
        graphics.lineBetween(centerX - compassSize, centerY, centerX + compassSize, centerY);
        graphics.lineStyle(1, 0x92400e, 0.6);
        graphics.lineBetween(centerX - compassSize * 0.7, centerY - compassSize * 0.7, centerX + compassSize * 0.7, centerY + compassSize * 0.7);
        graphics.lineBetween(centerX - compassSize * 0.7, centerY + compassSize * 0.7, centerX + compassSize * 0.7, centerY - compassSize * 0.7);
        graphics.fillStyle(0xdc2626, 1);
        graphics.fillTriangle(centerX, centerY - compassSize - 2, centerX - 2, centerY - compassSize + 2, centerX + 2, centerY - compassSize + 2);
        const drawFlag = (x: number, y: number) => {
          graphics.lineStyle(1, 0x92400e, 1);
          graphics.lineBetween(x, y, x, y - 8);
          graphics.fillStyle(0xef4444, 1);
          graphics.fillTriangle(x, y - 8, x, y - 5, x + 5, y - 6.5);
        };
        const tlX = (centerTileX - 1) * TILE_SIZE + TILE_SIZE - 3;
        const tlY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE - 3;
        const trX = (centerTileX + 1) * TILE_SIZE + 3;
        const trY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE - 3;
        drawFlag(tlX, tlY);
        drawFlag(trX, trY);
      });
    });
    
    // Store first tile hit area for backward compatibility (used for cleanup)
    const hitArea = tileHitAreas[0];
    
    this.locations.set(island.id, { graphics, x: island.x, y: island.y, islandData: island, hitArea, label, typeLabel, safeLabel });
  }

  private createTavernSprite(island: IslandData): void {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(3);

    const centerTileX = Math.floor(island.x / TILE_SIZE);
    const centerTileY = Math.floor(island.y / TILE_SIZE);

    const plusTiles = [
      { dx: 0, dy: -1 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 0 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
    ];

    const safeRadiusTiles = 8;

    const drawTiles = (fillColor: number, borderColor: number, borderWidth: number): void => {
      plusTiles.forEach((tile) => {
        const worldX = (centerTileX + tile.dx) * TILE_SIZE;
        const worldY = (centerTileY + tile.dy) * TILE_SIZE;
        graphics.fillStyle(fillColor, 1);
        graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
        graphics.lineStyle(borderWidth, borderColor, 1);
        graphics.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
      });
    };

    const isInsideRift = this.scene.registry.get('riftId') && this.scene.registry.get('riftId') !== 'core';
    if (!isInsideRift) {
      drawSafeZone(graphics, island.x, island.y, safeRadiusTiles, {
        fillColor: 0x8b5a2b,
        ringColor: 0x6f4518,
      });
    }
    drawTiles(TAVERN_COLOR, TAVERN_BORDER, 1);

    const centerX = centerTileX * TILE_SIZE + TILE_SIZE / 2;
    const centerY = centerTileY * TILE_SIZE + TILE_SIZE / 2;
    
    // Add decorative elements for Tavern
    // Warm glow circles in corners
    graphics.fillStyle(0xfbbf24, 0.2);
    graphics.fillCircle(centerX - 10, centerY - 10, 6);
    graphics.fillCircle(centerX + 10, centerY - 10, 6);
    graphics.fillStyle(0xfbbf24, 0.3);
    graphics.fillCircle(centerX - 10, centerY - 10, 4);
    graphics.fillCircle(centerX + 10, centerY - 10, 4);
    
    // Beer mug in center
    const mugX = centerX - 4;
    const mugY = centerY;
    // Mug body
    graphics.fillStyle(0xd4a574, 1);
    graphics.fillRect(mugX, mugY - 6, 8, 8);
    graphics.lineStyle(1, 0x8b6f47, 1);
    graphics.strokeRect(mugX, mugY - 6, 8, 8);
    // Handle
    graphics.lineStyle(1.5, 0x8b6f47, 1);
    graphics.strokeCircle(mugX + 9, mugY - 2, 3);
    // Foam/head
    graphics.fillStyle(0xfef3c7, 1);
    graphics.fillEllipse(mugX + 4, mugY - 6, 5, 2);
    
    // Small lanterns on left and right tiles
    const drawLantern = (x: number, y: number) => {
      // Lantern body
      graphics.fillStyle(0x78350f, 0.8);
      graphics.fillRect(x - 3, y - 4, 6, 6);
      graphics.lineStyle(1, 0x451a03, 1);
      graphics.strokeRect(x - 3, y - 4, 6, 6);
      // Glow
      graphics.fillStyle(0xfbbf24, 0.4);
      graphics.fillCircle(x, y - 1, 4);
      graphics.fillStyle(0xfbbf24, 0.6);
      graphics.fillCircle(x, y - 1, 2);
      // Top hook
      graphics.lineStyle(1, 0x451a03, 1);
      graphics.lineBetween(x, y - 4, x, y - 6);
    };
    
    const leftLanternX = (centerTileX - 1) * TILE_SIZE + TILE_SIZE / 2;
    const rightLanternX = (centerTileX + 1) * TILE_SIZE + TILE_SIZE / 2;
    const lanternY = centerTileY * TILE_SIZE + 8;
    drawLantern(leftLanternX, lanternY);
    drawLantern(rightLanternX, lanternY);

    let safeLabel: Phaser.GameObjects.Text | undefined;
    if (!isInsideRift) {
      safeLabel = this.scene.add.text(centerX, centerY - TILE_SIZE * 2, 'SAFE ZONE', {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '8px',
        color: '#8b5a2b',
        backgroundColor: '#000000aa',
        padding: { x: 2, y: 1 },
      });
      safeLabel.setOrigin(0.5);
      safeLabel.setDepth(4);
    }

    const displayName = island.permanentName || 'Tavern';
    const label = this.scene.add.text(centerX, centerY + TILE_SIZE * 1.5, displayName, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '9px',
      color: '#ffffff',
      backgroundColor: '#8b5a2bcc',
      padding: { x: 3, y: 1 },
    });
    label.setOrigin(0.5);
    label.setDepth(4);

    const typeLabel = this.scene.add.text(centerX, centerY + TILE_SIZE * 2.5, 'Tavern', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '7px',
      color: '#aaaaaa',
      backgroundColor: '#000000aa',
      padding: { x: 2, y: 1 },
    });
    typeLabel.setOrigin(0.5);
    typeLabel.setDepth(4);

    // Create interactive hit areas ONLY for the colored building tiles (not the safe zone)
    // Safe zone is visual only - clicks should work on water outside it
    const tileHitAreas: Phaser.GameObjects.Rectangle[] = [];
    plusTiles.forEach(tile => {
      const tileWorldX = (centerTileX + tile.dx) * TILE_SIZE;
      const tileWorldY = (centerTileY + tile.dy) * TILE_SIZE;
      const tileHitArea = this.scene.add.rectangle(tileWorldX + TILE_SIZE / 2, tileWorldY + TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 0x000000, 0);
      tileHitArea.setInteractive();
      tileHitArea.setDepth(6); // Same depth as resource nodes and fishing spots
      tileHitAreas.push(tileHitArea);
      
      // Consolidated click handler for both left and right clicks on individual tiles
      tileHitArea.on('pointerdown', (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        // Stop propagation using both Phaser event and native DOM event
        event.stopPropagation();
        if (pointer.event && typeof pointer.event.stopPropagation === 'function') {
          pointer.event.stopPropagation();
        }
        if (pointer.event && typeof pointer.event.preventDefault === 'function') {
          pointer.event.preventDefault();
        }
        
        if (pointer.rightButtonDown() && this.callbacks.onIslandRightClick) {
          // Right-click handler for context menu
          const camera = this.scene.cameras.main;
          const screenX = pointer.worldX - camera.scrollX;
          const screenY = pointer.worldY - camera.scrollY;
          this.callbacks.onIslandRightClick(island, screenX, screenY);
          return;
        }
        
        if (pointer.leftButtonDown()) {
          // Left click: dock at tavern
          console.log(`🍺 Tavern tile clicked: ${island.permanentName}`);
          this.callbacks.onTavernClick?.(island, pointer.worldX, pointer.worldY);
        }
      });
      
      // Hover effects on building tiles
      tileHitArea.on('pointerover', () => {
        graphics.clear();
        drawSafeZone(graphics, island.x, island.y, safeRadiusTiles, {
          fillColor: 0xa1743d,
          ringColor: 0x7a4f1f,
        });
        drawTiles(0xa1743d, 0x7a4f1f, 2);
        // Redraw decorations
        graphics.fillStyle(0xfbbf24, 0.2);
        graphics.fillCircle(centerX - 10, centerY - 10, 6);
        graphics.fillCircle(centerX + 10, centerY - 10, 6);
        graphics.fillStyle(0xfbbf24, 0.3);
        graphics.fillCircle(centerX - 10, centerY - 10, 4);
        graphics.fillCircle(centerX + 10, centerY - 10, 4);
        const mugX = centerX - 4;
        const mugY = centerY;
        graphics.fillStyle(0xd4a574, 1);
        graphics.fillRect(mugX, mugY - 6, 8, 8);
        graphics.lineStyle(1, 0x8b6f47, 1);
        graphics.strokeRect(mugX, mugY - 6, 8, 8);
        graphics.lineStyle(1.5, 0x8b6f47, 1);
        graphics.strokeCircle(mugX + 9, mugY - 2, 3);
        graphics.fillStyle(0xfef3c7, 1);
        graphics.fillEllipse(mugX + 4, mugY - 6, 5, 2);
        const drawLantern = (x: number, y: number) => {
          graphics.fillStyle(0x78350f, 0.8);
          graphics.fillRect(x - 3, y - 4, 6, 6);
          graphics.lineStyle(1, 0x451a03, 1);
          graphics.strokeRect(x - 3, y - 4, 6, 6);
          graphics.fillStyle(0xfbbf24, 0.4);
          graphics.fillCircle(x, y - 1, 4);
          graphics.fillStyle(0xfbbf24, 0.6);
          graphics.fillCircle(x, y - 1, 2);
          graphics.lineStyle(1, 0x451a03, 1);
          graphics.lineBetween(x, y - 4, x, y - 6);
        };
        const leftLanternX = (centerTileX - 1) * TILE_SIZE + TILE_SIZE / 2;
        const rightLanternX = (centerTileX + 1) * TILE_SIZE + TILE_SIZE / 2;
        const lanternY = centerTileY * TILE_SIZE + 8;
        drawLantern(leftLanternX, lanternY);
        drawLantern(rightLanternX, lanternY);
      });
      
      tileHitArea.on('pointerout', () => {
        graphics.clear();
        drawSafeZone(graphics, island.x, island.y, safeRadiusTiles, {
          fillColor: 0x8b5a2b,
          ringColor: 0x6f4518,
        });
        drawTiles(TAVERN_COLOR, TAVERN_BORDER, 1);
        // Redraw decorations
        graphics.fillStyle(0xfbbf24, 0.2);
        graphics.fillCircle(centerX - 10, centerY - 10, 6);
        graphics.fillCircle(centerX + 10, centerY - 10, 6);
        graphics.fillStyle(0xfbbf24, 0.3);
        graphics.fillCircle(centerX - 10, centerY - 10, 4);
        graphics.fillCircle(centerX + 10, centerY - 10, 4);
        const mugX = centerX - 4;
        const mugY = centerY;
        graphics.fillStyle(0xd4a574, 1);
        graphics.fillRect(mugX, mugY - 6, 8, 8);
        graphics.lineStyle(1, 0x8b6f47, 1);
        graphics.strokeRect(mugX, mugY - 6, 8, 8);
        graphics.lineStyle(1.5, 0x8b6f47, 1);
        graphics.strokeCircle(mugX + 9, mugY - 2, 3);
        graphics.fillStyle(0xfef3c7, 1);
        graphics.fillEllipse(mugX + 4, mugY - 6, 5, 2);
        const drawLantern = (x: number, y: number) => {
          graphics.fillStyle(0x78350f, 0.8);
          graphics.fillRect(x - 3, y - 4, 6, 6);
          graphics.lineStyle(1, 0x451a03, 1);
          graphics.strokeRect(x - 3, y - 4, 6, 6);
          graphics.fillStyle(0xfbbf24, 0.4);
          graphics.fillCircle(x, y - 1, 4);
          graphics.fillStyle(0xfbbf24, 0.6);
          graphics.fillCircle(x, y - 1, 2);
          graphics.lineStyle(1, 0x451a03, 1);
          graphics.lineBetween(x, y - 4, x, y - 6);
        };
        const leftLanternX = (centerTileX - 1) * TILE_SIZE + TILE_SIZE / 2;
        const rightLanternX = (centerTileX + 1) * TILE_SIZE + TILE_SIZE / 2;
        const lanternY = centerTileY * TILE_SIZE + 8;
        drawLantern(leftLanternX, lanternY);
        drawLantern(rightLanternX, lanternY);
      });
    });
    
    // Store first tile hit area for backward compatibility (used for cleanup)
    const hitArea = tileHitAreas[0];
    
    this.locations.set(island.id, { graphics, x: island.x, y: island.y, islandData: island, hitArea, label, typeLabel, safeLabel });
  }

  private createShipyardSprite(island: IslandData): void {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(3);
    
    const centerTileX = Math.floor(island.x / TILE_SIZE);
    const centerTileY = Math.floor(island.y / TILE_SIZE);
    
    // Shipyard uses a different shape - anchor/dock pattern
    const shipyardTiles = [
      { dx: 0, dy: -1 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 0 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
    ];
    
    const safeRadiusTiles = 8; // Safe zone radius in tiles (reduced from 10)
    
    const drawTiles = (fillColor: number, borderColor: number, borderWidth: number): void => {
      shipyardTiles.forEach(tile => {
        const worldX = (centerTileX + tile.dx) * TILE_SIZE;
        const worldY = (centerTileY + tile.dy) * TILE_SIZE;
        graphics.fillStyle(fillColor, 1);
        graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
        graphics.lineStyle(borderWidth, borderColor, 1);
        graphics.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
      });
    };
    
    // Draw grid-based safe zone (faded squares)
    const isInsideRift = this.scene.registry.get('riftId') && this.scene.registry.get('riftId') !== 'core';
    
    let safeZoneGraphics: Phaser.GameObjects.Graphics | undefined;
    if (!isInsideRift) {
      safeZoneGraphics = this.scene.add.graphics();
      safeZoneGraphics.setDepth(2);
      drawSafeZone(safeZoneGraphics, island.x, island.y, safeRadiusTiles);
    }
    
    drawTiles(SHIPYARD_COLOR, SHIPYARD_BORDER, 1);
    
    const postCenterX = centerTileX * TILE_SIZE + TILE_SIZE / 2;
    const postCenterY = centerTileY * TILE_SIZE + TILE_SIZE / 2;
    
    // Add decorative elements for Shipyard
    // Crane structure in center
    const craneBaseX = postCenterX - 6;
    const craneBaseY = postCenterY + 8;
    const craneHeight = 16;
    
    // Crane base and vertical beam
    graphics.lineStyle(2, 0xcc6600, 1);
    graphics.lineBetween(craneBaseX, craneBaseY, craneBaseX, craneBaseY - craneHeight);
    // Crane arm
    graphics.lineBetween(craneBaseX, craneBaseY - craneHeight, craneBaseX + 12, craneBaseY - craneHeight + 4);
    // Hanging line
    graphics.lineStyle(1, 0x8b4513, 1);
    graphics.lineBetween(craneBaseX + 10, craneBaseY - craneHeight + 4, craneBaseX + 10, craneBaseY - 4);
    // Hook
    graphics.strokeCircle(craneBaseX + 10, craneBaseY - 4, 2);
    
    // Wooden planks stacked on left tile
    const plankStartX = (centerTileX - 1) * TILE_SIZE + 4;
    const plankY = centerTileY * TILE_SIZE + TILE_SIZE - 4;
    graphics.fillStyle(0x8b4513, 1);
    graphics.fillRect(plankStartX, plankY, 12, 2);
    graphics.fillRect(plankStartX, plankY - 3, 12, 2);
    graphics.fillRect(plankStartX, plankY - 6, 12, 2);
    graphics.lineStyle(1, 0x654321, 1);
    graphics.strokeRect(plankStartX, plankY, 12, 2);
    graphics.strokeRect(plankStartX, plankY - 3, 12, 2);
    graphics.strokeRect(plankStartX, plankY - 6, 12, 2);
    
    // Tools on right tile (hammer and wrench)
    const toolX = (centerTileX + 1) * TILE_SIZE + TILE_SIZE / 2;
    const toolY = centerTileY * TILE_SIZE + TILE_SIZE - 8;
    // Hammer
    graphics.fillStyle(0x8b4513, 1);
    graphics.fillRect(toolX - 6, toolY + 2, 2, 8); // Handle
    graphics.fillStyle(0x708090, 1);
    graphics.fillRect(toolX - 8, toolY, 6, 3); // Head
    graphics.lineStyle(1, 0x2f4f4f, 1);
    graphics.strokeRect(toolX - 8, toolY, 6, 3);
    // Wrench
    graphics.lineStyle(2, 0x708090, 1);
    graphics.lineBetween(toolX + 2, toolY + 8, toolX + 2, toolY + 2);
    graphics.strokeCircle(toolX + 2, toolY + 1, 2);
    
    // Anchor icon (smaller, top tile)
    const anchorX = postCenterX;
    const anchorY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE / 2;
    graphics.lineStyle(2, 0x654321, 1);
    // Anchor shank
    graphics.lineBetween(anchorX, anchorY - 6, anchorX, anchorY + 4);
    // Anchor ring
    graphics.strokeCircle(anchorX, anchorY - 6, 2);
    // Anchor flukes
    graphics.lineBetween(anchorX - 5, anchorY + 2, anchorX, anchorY + 4);
    graphics.lineBetween(anchorX + 5, anchorY + 2, anchorX, anchorY + 4);
    // Fluke curves
    graphics.strokeCircle(anchorX - 5, anchorY + 2, 2);
    graphics.strokeCircle(anchorX + 5, anchorY + 2, 2);
    
    let safeLabel: Phaser.GameObjects.Text | undefined;
    if (!isInsideRift) {
      safeLabel = this.scene.add.text(postCenterX, postCenterY - TILE_SIZE * 2, 'SAFE ZONE', {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '8px',
        color: '#ffa500',
        backgroundColor: '#000000aa',
        padding: { x: 2, y: 1 },
      });
      safeLabel.setOrigin(0.5);
      safeLabel.setDepth(4);
    }
    
    // Use the actual shipyard name
    const displayName = island.permanentName || 'Shipyard';
    const label = this.scene.add.text(postCenterX, postCenterY + TILE_SIZE * 1.5, displayName, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '9px',
      color: '#ffffff',
      backgroundColor: '#ff8c00cc',
      padding: { x: 3, y: 1 },
    });
    label.setOrigin(0.5);
    label.setDepth(4);
    
    // Add secondary type label beneath the name
    const typeLabel = this.scene.add.text(postCenterX, postCenterY + TILE_SIZE * 2.5, 'Shipyard', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '7px',
      color: '#aaaaaa',
      backgroundColor: '#000000aa',
      padding: { x: 2, y: 1 },
    });
    typeLabel.setOrigin(0.5);
    typeLabel.setDepth(4);
    
    // Create interactive hit areas ONLY for the colored building tiles (not the safe zone)
    // Safe zone is visual only - clicks should work on water outside it
    const tileHitAreas: Phaser.GameObjects.Rectangle[] = [];
    shipyardTiles.forEach(tile => {
      const tileWorldX = (centerTileX + tile.dx) * TILE_SIZE;
      const tileWorldY = (centerTileY + tile.dy) * TILE_SIZE;
      const tileHitArea = this.scene.add.rectangle(tileWorldX + TILE_SIZE / 2, tileWorldY + TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 0x000000, 0);
      tileHitArea.setInteractive();
      tileHitArea.setDepth(6); // Same depth as resource nodes and fishing spots
      tileHitAreas.push(tileHitArea);
      
      // Consolidated click handler for both left and right clicks on individual tiles
      tileHitArea.on('pointerdown', (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        // Stop propagation using both Phaser event and native DOM event
        event.stopPropagation();
        if (pointer.event && typeof pointer.event.stopPropagation === 'function') {
          pointer.event.stopPropagation();
        }
        if (pointer.event && typeof pointer.event.preventDefault === 'function') {
          pointer.event.preventDefault();
        }
        
        if (pointer.rightButtonDown() && this.callbacks.onIslandRightClick) {
          // Right-click handler for context menu
          const camera = this.scene.cameras.main;
          const screenX = pointer.worldX - camera.scrollX;
          const screenY = pointer.worldY - camera.scrollY;
          this.callbacks.onIslandRightClick(island, screenX, screenY);
          return;
        }
        
        if (pointer.leftButtonDown()) {
          console.log(`⚓ Shipyard tile clicked: ${island.permanentName}`);
          if (this.callbacks.onShipyardClick) {
            this.callbacks.onShipyardClick(island, pointer.worldX, pointer.worldY);
          }
        }
      });
      
      // Hover effects on building tiles
      tileHitArea.on('pointerover', () => {
        graphics.clear();
        drawSafeZone(graphics, island.x, island.y, safeRadiusTiles);
        drawTiles(0xffcc80, 0xffb74d, 2);
        // Redraw decorations
        const craneBaseX = postCenterX - 6;
        const craneBaseY = postCenterY + 8;
        const craneHeight = 16;
        graphics.lineStyle(2, 0xcc6600, 1);
        graphics.lineBetween(craneBaseX, craneBaseY, craneBaseX, craneBaseY - craneHeight);
        graphics.lineBetween(craneBaseX, craneBaseY - craneHeight, craneBaseX + 12, craneBaseY - craneHeight + 4);
        graphics.lineStyle(1, 0x8b4513, 1);
        graphics.lineBetween(craneBaseX + 10, craneBaseY - craneHeight + 4, craneBaseX + 10, craneBaseY - 4);
        graphics.strokeCircle(craneBaseX + 10, craneBaseY - 4, 2);
        const plankStartX = (centerTileX - 1) * TILE_SIZE + 4;
        const plankY = centerTileY * TILE_SIZE + TILE_SIZE - 4;
        graphics.fillStyle(0x8b4513, 1);
        graphics.fillRect(plankStartX, plankY, 12, 2);
        graphics.fillRect(plankStartX, plankY - 3, 12, 2);
        graphics.fillRect(plankStartX, plankY - 6, 12, 2);
        const toolX = (centerTileX + 1) * TILE_SIZE + TILE_SIZE / 2;
        const toolY = centerTileY * TILE_SIZE + TILE_SIZE - 8;
        graphics.fillStyle(0x8b4513, 1);
        graphics.fillRect(toolX - 6, toolY + 2, 2, 8);
        graphics.fillStyle(0x708090, 1);
        graphics.fillRect(toolX - 8, toolY, 6, 3);
        graphics.lineStyle(2, 0x708090, 1);
        graphics.lineBetween(toolX + 2, toolY + 8, toolX + 2, toolY + 2);
        graphics.strokeCircle(toolX + 2, toolY + 1, 2);
        const anchorX = postCenterX;
        const anchorY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE / 2;
        graphics.lineStyle(2, 0x654321, 1);
        graphics.lineBetween(anchorX, anchorY - 6, anchorX, anchorY + 4);
        graphics.strokeCircle(anchorX, anchorY - 6, 2);
        graphics.lineBetween(anchorX - 5, anchorY + 2, anchorX, anchorY + 4);
        graphics.lineBetween(anchorX + 5, anchorY + 2, anchorX, anchorY + 4);
        graphics.strokeCircle(anchorX - 5, anchorY + 2, 2);
        graphics.strokeCircle(anchorX + 5, anchorY + 2, 2);
      });
      
      tileHitArea.on('pointerout', () => {
        graphics.clear();
        drawSafeZone(graphics, island.x, island.y, safeRadiusTiles);
        drawTiles(SHIPYARD_COLOR, SHIPYARD_BORDER, 1);
        // Redraw decorations
        const craneBaseX = postCenterX - 6;
        const craneBaseY = postCenterY + 8;
        const craneHeight = 16;
        graphics.lineStyle(2, 0xcc6600, 1);
        graphics.lineBetween(craneBaseX, craneBaseY, craneBaseX, craneBaseY - craneHeight);
        graphics.lineBetween(craneBaseX, craneBaseY - craneHeight, craneBaseX + 12, craneBaseY - craneHeight + 4);
        graphics.lineStyle(1, 0x8b4513, 1);
        graphics.lineBetween(craneBaseX + 10, craneBaseY - craneHeight + 4, craneBaseX + 10, craneBaseY - 4);
        graphics.strokeCircle(craneBaseX + 10, craneBaseY - 4, 2);
        const plankStartX = (centerTileX - 1) * TILE_SIZE + 4;
        const plankY = centerTileY * TILE_SIZE + TILE_SIZE - 4;
        graphics.fillStyle(0x8b4513, 1);
        graphics.fillRect(plankStartX, plankY, 12, 2);
        graphics.fillRect(plankStartX, plankY - 3, 12, 2);
        graphics.fillRect(plankStartX, plankY - 6, 12, 2);
        const toolX = (centerTileX + 1) * TILE_SIZE + TILE_SIZE / 2;
        const toolY = centerTileY * TILE_SIZE + TILE_SIZE - 8;
        graphics.fillStyle(0x8b4513, 1);
        graphics.fillRect(toolX - 6, toolY + 2, 2, 8);
        graphics.fillStyle(0x708090, 1);
        graphics.fillRect(toolX - 8, toolY, 6, 3);
        graphics.lineStyle(2, 0x708090, 1);
        graphics.lineBetween(toolX + 2, toolY + 8, toolX + 2, toolY + 2);
        graphics.strokeCircle(toolX + 2, toolY + 1, 2);
        const anchorX = postCenterX;
        const anchorY = (centerTileY - 1) * TILE_SIZE + TILE_SIZE / 2;
        graphics.lineStyle(2, 0x654321, 1);
        graphics.lineBetween(anchorX, anchorY - 6, anchorX, anchorY + 4);
        graphics.strokeCircle(anchorX, anchorY - 6, 2);
        graphics.lineBetween(anchorX - 5, anchorY + 2, anchorX, anchorY + 4);
        graphics.lineBetween(anchorX + 5, anchorY + 2, anchorX, anchorY + 4);
        graphics.strokeCircle(anchorX - 5, anchorY + 2, 2);
        graphics.strokeCircle(anchorX + 5, anchorY + 2, 2);
      });
    });
    
    // Store first tile hit area for backward compatibility (used for cleanup)
    const hitArea = tileHitAreas[0];
    
    this.locations.set(island.id, { graphics, x: island.x, y: island.y, islandData: island, hitArea, safeLabel, safeZoneGraphics });
  }
}

